import { useEffect, useState } from "react";
import styling from "./Icon.module.scss";
import clsx from "clsx";
import { type IconName } from "@scaleaq/sui-icons/icon-types";

type IconSize = "large" | "medium" | "small" | "tiny" | "device" | "products";

function loadSet(size: IconSize) {
	switch (size) {
		case "large":
			return import("@scaleaq/sui-icons/react/large");
		case "medium":
			return import("@scaleaq/sui-icons/react/medium");
		case "small":
			return import("@scaleaq/sui-icons/react/small");
		case "tiny":
			return import("@scaleaq/sui-icons/react/tiny");
		case "device":
			return import("@scaleaq/sui-icons/react/device");
		case "products":
			return import("@scaleaq/sui-icons/react/products");
	}
}

/**
 * Loads an icon by its full IconName (e.g., "LargeHome", "SmallChevronUp").
 * The icon set is determined by the name prefix, and the component is looked up by the stripped name.
 */
async function loadIcon(fullName: IconName) {
	// Determine which set to load from based on prefix
	const iconSet = getIconSetFromName(fullName);
	const componentName = stripPrefix(fullName);

	const mod: any = await loadSet(iconSet);
	const C = mod[componentName] ?? mod.default ?? null;
	if (!C) {
		console.error(`[Icon] Icon not found. FullName: ${fullName}, Set: ${iconSet}, Component: ${componentName}`);
		console.error(`[Icon] Available exports:`, Object.keys(mod).slice(0, 10));
		throw new Error(`Icon not found: ${fullName}`);
	}
	return C;
}

/**
 * Determines which icon set to load based on the icon name prefix.
 */
function getIconSetFromName(name: IconName): IconSize {
	if (name.startsWith("Large")) return "large";
	if (name.startsWith("Medium")) return "medium";
	if (name.startsWith("Small")) return "small";
	if (name.startsWith("Tiny")) return "tiny";
	if (name.startsWith("Device")) return "device";
	if (name.startsWith("Products")) return "products";
	// Fallback to medium
	return "medium";
}

/**
 * Strips the prefix from the icon name to get the component name.
 * E.g., "LargeHome" -> "Home", "SmallChevronUp" -> "ChevronUp", "DeviceAlarmBell" -> "AlarmBell"
 */
function stripPrefix(name: IconName): string {
	const prefixes = ["Large", "Medium", "Small", "Tiny", "Device", "Products"];
	for (const prefix of prefixes) {
		if (name.startsWith(prefix)) {
			return name.substring(prefix.length);
		}
	}
	return name;
}

export namespace Icon {
	export type Props = React.SVGProps<SVGSVGElement> & {
		name: IconName;
		size?: IconSize; // Optional CSS size override (only affects styling, not which icon loads)
		fallback?: React.ReactNode; // optional fallback if not found
		secondary?: boolean;
	};
}

export const Icon: React.FC<Icon.Props> = (props: Icon.Props) => {
	const { name, size, fallback = null, secondary = false, ...svgProps } = props;
	// Determine display size: use provided size or derive from icon name
	const displaySize = size ?? getIconSetFromName(name);
	const [Comp, setComp] = useState<React.ComponentType<any> | null>(null);

	useEffect(() => {
		let ok = true;
		loadIcon(name)
			.then((C) => ok && setComp(() => C))
			.catch((err) => {
				console.error("[Icon] Failed to load", { name, err });
				if (ok) setComp(null);
			});
		return () => {
			ok = false;
		};
	}, [name]);

	if (!Comp) return <>{fallback}</>;

	const ariaHidden = svgProps["aria-label"] == null && svgProps["aria-labelledby"] == null;

	return (
		<div className={clsx(styling.icon, {
			[styling[`size--${displaySize}`]]: displaySize,
			[styling["secondary"]]: secondary,
		})}>
			<Comp aria-hidden={ariaHidden} {...svgProps} />
		</div>
	);
};
